import { useMemo } from 'react';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';

import ApiState from '@/components/ApiState/ApiState';
import ChapterNav from '@/components/ChapterNav/ChapterNav';
import Layout from '@/components/Layout/Layout';
import Overlay from '@/components/Overlay/Overlay';
import { getImageUrl } from '@/utils/dedaloUtils';
import { Photos, Audiovisuals, Documents } from '@/components/Resources';
import { useChapter } from '@/api/useThesaurusTerm';
import { useFragments, useAudiovisualTitles } from '@/api/useFragments';
import { useAdjacentPages } from '@/store/useAdjacentPages';

import segellsColor from '@/assets/img/overlays/segells-color@2x.png';

function Chapter({ page }) {
    const { t } = useTranslation();
    const { prev, next } = useAdjacentPages(page.web_path);
    const { data, isLoading, error } = useChapter(page.section_id);
    const chapterContent = data?.[0];
    const { title, body, image, other_images, audiovisual, indexations, pdf_data } =
        chapterContent ?? {};
    const identifyingImage = image?.[0] ?? {};

    const parsedIndexations = useMemo(() => {
        if (!indexations) return [];
        try {
            return JSON.parse(indexations);
        } catch {
            return [];
        }
    }, [indexations]);

    const { data: fragments } = useFragments(parsedIndexations);
    const { data: avTitles } = useAudiovisualTitles(parsedIndexations.map((l) => l.section_id));

    const indexationVideos = (fragments ?? [])
        .map((fragment, i) =>
            fragment
                ? {
                      title: avTitles?.[i]?.title ?? '',
                      video: fragment.video_url,
                      fragm: fragment.fragm ?? '',
                  }
                : null
        )
        .filter(Boolean);

    const allVideos = [...(audiovisual ?? []), ...indexationVideos];
    const hasResources = !!other_images?.length || !!allVideos.length;

    return (
        <Layout>
            <title>{page.term}</title>
            <div className="overlay-space is-hidden-touch">
                <Overlay
                    src={segellsColor}
                    style={{
                        top: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 8,
                        maxWidth: 486,
                    }}
                />
            </div>
            <ApiState isLoading={isLoading} error={error}>
                <div className="wrapper py-8">
                    <div className="columns is-desktop">
                        <div className="column is-two-fifths-desktop">
                            <div className="flow has-text-weight-light">
                                {title && (
                                    <h1 className="has-text-primary is-uppercase is-family-secondary">
                                        {title}
                                    </h1>
                                )}
                                <figure className="is-hidden-desktop full-bleed--img">
                                    <img
                                        src={getImageUrl(identifyingImage.image)}
                                        alt={identifyingImage.footprint || ''}
                                    />
                                    {identifyingImage.footprint && (
                                        <figcaption className="is-size-6">
                                            {identifyingImage.footprint}
                                        </figcaption>
                                    )}
                                </figure>
                                {body && <div className="chapter-body">{parse(body)}</div>}
                            </div>
                        </div>
                        <div className="column is-three-fifths is-hidden-touch">
                            <figure className="mt-9">
                                <img
                                    src={getImageUrl(identifyingImage.image)}
                                    alt={identifyingImage.footprint || ''}
                                    className="radius"
                                />
                                {identifyingImage.footprint && (
                                    <figcaption className="is-size-6">
                                        {identifyingImage.footprint}
                                    </figcaption>
                                )}
                            </figure>
                        </div>
                    </div>
                    {hasResources && (
                        <div className="flow--2xs mt-8">
                            <h2 className="is-family-secondary has-text-primary is-uppercase">
                                {t('recursos')}
                            </h2>
                            <div className="chapter-resources">
                                {other_images && other_images.length > 0 && (
                                    <Photos images={other_images} />
                                )}
                                {allVideos.length > 0 && <Audiovisuals videos={allVideos} />}
                                {pdf_data && pdf_data.length > 0 && (
                                    <Documents documents={pdf_data} />
                                )}
                            </div>
                        </div>
                    )}

                    <ChapterNav prev={prev} next={next} />
                </div>
            </ApiState>
        </Layout>
    );
}

export default Chapter;
